// Motor del Snake: lógica pura y determinista.
// La comida sale de una semilla y la partida avanza a pasos (ticks), así que el servidor puede repetirla
// a partir de los giros del jugador para calcular la puntuación real (ver backend/src/games/snakeEngine.ts,
// que es una copia exacta de este archivo: si cambias uno, cambia el otro).

export interface Grid {
  cols: number;
  rows: number;
}

// Mismo tablero para todos: 20 × 20 con paredes
export const GRID: Grid = { cols: 20, rows: 20 };
export const START_LENGTH = 4;

// Dirección: 0 arriba, 1 derecha, 2 abajo, 3 izquierda
export type Dir = 0 | 1 | 2 | 3;
const DX = [0, 1, 0, -1];
const DY = [-1, 0, 1, 0];

export const isOpposite = (a: Dir, b: Dir) => (a + 2) % 4 === b;

// Velocidad: cada punto acorta el paso un 3,5 % (de 130 ms hasta un mínimo de 50 ms)
export function stepMs(score: number): number {
  return Math.max(50, Math.round(130 * Math.pow(0.965, score)));
}

// ─── Azar determinista ────────────────────────────────────────────────────────

// Hash de la semilla (xmur3) para iniciar el generador
function seedToInt(seed: string): number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

// ─── Partida ─────────────────────────────────────────────────────────────────

export interface SnakeState {
  grid: Grid;
  body: number[];   // casillas de la serpiente, de la cabeza a la cola
  dir: Dir;
  food: number;     // casilla de la comida (-1 si el tablero está lleno)
  score: number;
  tick: number;
  alive: boolean;
  won: boolean;     // llenó el tablero
  ate: boolean;     // comió en el último paso (la cola no avanzó)
  rng: number;      // estado del generador (mulberry32)
}

// Giro del jugador: en el paso k la serpiente gira hacia d
export interface Turn {
  k: number;
  d: Dir;
}

function rand(s: SnakeState): number {
  const a = (s.rng + 0x6d2b79f5) | 0;
  s.rng = a;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function placeFood(s: SnakeState): number {
  const total = s.grid.cols * s.grid.rows;
  const free = total - s.body.length;
  if (free <= 0) return -1;
  const taken = new Set(s.body);
  let r = Math.floor(rand(s) * free);
  for (let i = 0; i < total; i++) {
    if (taken.has(i)) continue;
    if (r === 0) return i;
    r--;
  }
  return -1;
}

export function newGame(seed: string, grid: Grid = GRID): SnakeState {
  const y = Math.floor(grid.rows / 2);
  const x = Math.floor(grid.cols / 2) - 2;
  const body = Array.from({ length: START_LENGTH }, (_, k) => y * grid.cols + x - k);
  const s: SnakeState = { grid, body, dir: 1, food: -1, score: 0, tick: 0, alive: true, won: false, ate: false, rng: seedToInt(seed) };
  s.food = placeFood(s);
  return s;
}

// Avanza un paso (modifica el estado). d: giro pedido para este paso, si lo hay.
export function step(s: SnakeState, d?: Dir): void {
  if (!s.alive) return;
  if (d !== undefined && d !== s.dir && !isOpposite(d, s.dir)) s.dir = d;
  const { cols, rows } = s.grid;
  const head = s.body[0];
  const nx = (head % cols) + DX[s.dir];
  const ny = Math.floor(head / cols) + DY[s.dir];
  s.tick++;
  s.ate = false;

  // Pared
  if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) { s.alive = false; return; }

  const next = ny * cols + nx;
  const eating = next === s.food;
  // Contra sí misma (la punta de la cola se aparta en este paso, salvo si come)
  const check = eating ? s.body.length : s.body.length - 1;
  for (let i = 0; i < check; i++) {
    if (s.body[i] === next) { s.alive = false; return; }
  }

  s.body.unshift(next);
  if (eating) {
    s.score++;
    s.ate = true;
    s.food = placeFood(s);
    if (s.food < 0) { s.won = true; s.alive = false; }
  } else {
    s.body.pop();
  }
}

export const MAX_TICKS = 250_000;

// Repite una partida a partir de sus giros (lo usa el servidor para calcular la puntuación).
// Tras el último giro la serpiente sigue recta hasta chocar, así que la partida siempre termina.
// Devuelve null si los giros no van en orden. durationMs: tiempo real que dura la partida a esa velocidad.
export function replay(seed: string, turns: Turn[], grid: Grid = GRID): { state: SnakeState; durationMs: number } | null {
  for (let k = 0; k < turns.length; k++) {
    const t = turns[k];
    if (!Number.isInteger(t.k) || t.k < 0 || (k > 0 && t.k <= turns[k - 1].k)) return null;
    if (![0, 1, 2, 3].includes(t.d)) return null;
  }
  const s = newGame(seed, grid);
  let next = 0;
  let durationMs = 0;
  while (s.alive && s.tick < MAX_TICKS) {
    durationMs += stepMs(s.score);
    const turn = turns[next] && turns[next].k === s.tick ? turns[next++] : undefined;
    step(s, turn?.d);
  }
  return { state: s, durationMs };
}
